"use client";
import { useMemo, useState } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Search, FileText } from "lucide-react";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import {
  enterpriseBrands,
  brandCategories,
  brandHasPage,
  brandHref,
  brandMonograms,
} from "@/content/enterprise/brands";
import { hubFaqs } from "@/content/enterprise/hub-faqs";

// The buying-flow strip (imagery rule I2): an informational diagram rendered as
// crawlable HTML in design tokens, not a raster image, never decorative.
const buyingSteps = [
  { step: "01", title: "Enquiry", detail: "Brand, quantity and use case — two minutes on the form." },
  { step: "02", title: "Quotation in RM", detail: "Priced locally, usually within two working days." },
  { step: "03", title: "Purchase order", detail: "Standard PO terms; MyInvois e-invoice issued." },
  { step: "04", title: "Authorised supply", detail: "Sourced through authorised Malaysian distribution." },
  { step: "05", title: "Delivery & licensing", detail: "Licence issuance or hardware delivery, tracked." },
  { step: "06", title: "Renewals handled", detail: "Co-termed and diarised — we chase the dates, not you." },
];

function groupId(group: string): string {
  return `group-${group.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
}

export default function EnterpriseHub() {
  const locale = useLocale();
  const reduce = useReducedMotion();
  const inView = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.5 },
      };
  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP). Below-the-fold sections keep the inView fade.
  const heroIn = reduce
    ? {}
    : {
        initial: { y: 12 },
        animate: { y: 0 },
        transition: { duration: 0.5 },
      };
  const lp = (href: string) => getLocalizedPathname(href, locale);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<string | null>(null);
  // Search matches name, blurb and group so "GPU", "cloud" or "licences" find
  // the right brand even when the visitor does not know the vendor name.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return enterpriseBrands.filter((b) => {
      if (activeCat && b.category !== activeCat) return false;
      if (!q) return true;
      return (
        b.name.toLowerCase().includes(q) ||
        b.blurb.toLowerCase().includes(q) ||
        b.category.toLowerCase().includes(q)
      );
    });
  }, [query, activeCat]);
  // Rendered by group, in brandCategories order; empty groups are skipped.
  const grouped = useMemo(
    () =>
      brandCategories
        .map((cat) => ({ cat, brands: filtered.filter((b) => b.category === cat) }))
        .filter((g) => g.brands.length > 0),
    [filtered],
  );

  return (
    <main className="bg-background">
      {/* ── Hero ── */}
      <section className="pb-14 pt-24 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...heroIn} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Enterprise Solutions")}
            </p>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-6xl">
              {t(locale, "Enterprise software and hardware, quoted in RM.")}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-neutral-muted md:text-xl">
              {t(
                locale,
                "Your IT supplier for a focused set of enterprise brands in Malaysia: AI compute, cloud, software licences and hardware. We specialise in orders of 10+ units or seats, or RM5,000+ a month, with SST and MyInvois handled by one local team.",
              )}
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link
                href={lp("/enterprise/enquiry/")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, "Request a quotation")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <a
                href="#brands"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 px-8 py-3 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/5"
              >
                {t(locale, "Browse all brands")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Buying flow (I2 diagram, crawlable) ── */}
      <section id="how-it-works" className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...heroIn}>
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "How buying works in Malaysia")}
            </h2>
            <p className="mt-3 max-w-2xl text-neutral-muted">
              {t(
                locale,
                "The same six steps for every brand in the directory — the paperwork most catalogue resellers leave you to discover is handled as part of the quote.",
              )}
            </p>
          </motion.div>
          <motion.ol {...heroIn} className="mt-10 grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {buyingSteps.map((s) => (
              <li key={s.step} className="bg-card p-6">
                <span className="font-display text-sm font-bold text-primary">{s.step}</span>
                <h3 className="mt-2 font-semibold text-neutral-emphasis">{t(locale, s.title)}</h3>
                <p className="mt-1 text-sm text-neutral-muted">{t(locale, s.detail)}</p>
              </li>
            ))}
          </motion.ol>
        </div>
      </section>

      {/* ── Brand directory: grouped, searchable card grid ── */}
      <section id="brands" className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView}>
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "The brand directory")}
            </h2>
            <p className="mt-3 max-w-2xl text-neutral-muted">
              {t(
                locale,
                "Every brand below is quotable today. Brands with a dedicated page carry licensing detail and buying guidance; for the rest, go straight to the enquiry form.",
              )}
            </p>
          </motion.div>

          {/* search + group filter */}
          <motion.div {...inView} className="mt-8 flex flex-col gap-4">
            <div className="relative max-w-md">
              <Search
                className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-muted"
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t(locale, "Search brands, e.g. OpenAI, Red Hat, GPU")}
                aria-label={t(locale, "Search brands")}
                className="w-full rounded-full border border-border bg-card py-2.5 pl-11 pr-4 text-sm text-neutral-emphasis placeholder:text-neutral-muted/70 focus:border-primary focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveCat(null)}
                className={
                  activeCat === null
                    ? "rounded-full border border-primary bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary"
                    : "rounded-full border border-border bg-card px-4 py-1.5 text-sm text-neutral-muted transition hover:border-primary/40 hover:text-primary"
                }
              >
                {t(locale, "All")}
              </button>
              {brandCategories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCat(activeCat === cat ? null : cat)}
                  className={
                    activeCat === cat
                      ? "rounded-full border border-primary bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary"
                      : "rounded-full border border-border bg-card px-4 py-1.5 text-sm text-neutral-muted transition hover:border-primary/40 hover:text-primary"
                  }
                >
                  {t(locale, cat)}
                </button>
              ))}
            </div>
            <p className="text-sm text-neutral-muted" aria-live="polite">
              {filtered.length} {t(locale, "of")} {enterpriseBrands.length} {t(locale, "brands")}
            </p>
          </motion.div>

          {grouped.length > 0 ? (
            <div className="mt-10 space-y-12">
              {grouped.map((g) => (
                <section key={g.cat} aria-labelledby={groupId(g.cat)}>
                  <h3
                    id={groupId(g.cat)}
                    className="font-display text-xl font-semibold text-neutral-emphasis"
                  >
                    {t(locale, g.cat)}
                  </h3>
                  <motion.ul {...inView} className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {g.brands.map((b) => {
                      const hasPage = brandHasPage(b);
                      return (
                        <li key={b.slug} className="relative h-full">
                          <Link
                            href={lp(brandHref(b))}
                            className="group flex h-full flex-col rounded-lg border border-border bg-card p-5 transition hover:border-primary/50 hover:shadow-sm"
                          >
                            <div className="flex items-start gap-4">
                              {/* Logo when we hold an approved asset; otherwise a
                                  designed monogram tile. Painted as a CSS mask so
                                  every brand renders in the same ink on ivory. */}
                              <span className="flex h-12 w-12 flex-none items-center justify-center overflow-hidden rounded-md border border-border bg-background">
                                {b.logo ? (
                                  <span
                                    aria-hidden
                                    className="h-7 w-9 bg-primary/75 transition group-hover:bg-primary"
                                    style={{
                                      maskImage: `url(/images/brands/${b.logo})`,
                                      WebkitMaskImage: `url(/images/brands/${b.logo})`,
                                      maskSize: "contain",
                                      WebkitMaskSize: "contain",
                                      maskRepeat: "no-repeat",
                                      WebkitMaskRepeat: "no-repeat",
                                      maskPosition: "center",
                                      WebkitMaskPosition: "center",
                                    }}
                                  />
                                ) : (
                                  <span className="font-display text-[0.95rem] font-semibold tracking-tight text-primary/70 transition group-hover:text-primary">
                                    {brandMonograms[b.slug]}
                                  </span>
                                )}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <h4 className="font-display text-lg font-semibold leading-tight text-neutral-emphasis">
                                    {b.name}
                                  </h4>
                                  {hasPage && (
                                    <span className="mt-1 inline-flex flex-none items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-primary">
                                      <FileText className="h-3 w-3" aria-hidden />
                                      {t(locale, "Details")}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1.5 text-sm leading-relaxed text-neutral-muted">
                                  {t(locale, b.blurb)}
                                </p>
                              </div>
                            </div>
                            <div className="flex-1" />
                            <div className="mt-4 flex items-center justify-end border-t border-border pt-3">
                              <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                                {hasPage ? t(locale, "View details") : t(locale, "Get a quote")}
                                <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" aria-hidden />
                              </span>
                            </div>
                          </Link>
                          {/* A second destination can't nest inside the card's
                              link, so it sits over the footer's empty left side. */}
                          {b.alsoLink && (
                            <Link
                              href={lp(b.alsoLink.href)}
                              className="absolute bottom-3 left-5 z-10 inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                            >
                              {t(locale, b.alsoLink.label)}
                              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </motion.ul>
                </section>
              ))}
            </div>
          ) : (
            <motion.div {...inView} className="mt-8 rounded-lg border border-border bg-card p-10 text-center">
              <p className="text-neutral-muted">
                {t(locale, "No brand matches that search — but we may still supply it.")}
              </p>
              <Link
                href={lp("/enterprise/enquiry/")}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, "Ask us about it")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </motion.div>
          )}

          <motion.p {...inView} className="mt-10 text-sm text-neutral-muted">
            {t(
              locale,
              "Brands are supplied through authorised channels. Brand names are the trademarks of their respective owners; a listing describes what we supply or procure for you, not a partnership status unless one is stated.",
            )}
          </motion.p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="border-t border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <StructuredData type="faq" faqs={hubFaqs} />
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Questions about enterprise supply")}
          </motion.h2>
          <div className="mt-8 space-y-6">
            {hubFaqs.map((f) => (
              <motion.div {...inView} key={f.question} className="rounded-lg border border-border bg-card p-6">
                <h3 className="font-semibold text-neutral-emphasis">{t(locale, f.question)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, f.answer)}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...inView} className="mt-10 text-center">
            <Link
              href={lp("/enterprise/enquiry/")}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {t(locale, "Request a quotation")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
